import { Component, OnDestroy } from '@angular/core'
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms'
import { PlotData, plotData$ } from '../../../state/plot-data/plot-data'
import { Subject, takeUntil } from 'rxjs'
import { DomainInfo } from '../../../state/plot-data/plot-data.state'
import { selectionActions } from '../../../state/selection/selection.actions'
import { Store } from '@ngrx/store'

@Component({
    selector: 'app-rectangular-selection',
    templateUrl: './rectangular-selection.component.html',
    styleUrls: ['./rectangular-selection.component.scss'],
})
export class RectangularSelectionComponent implements OnDestroy {
    readonly rectangularSelectionForm: FormGroup

    readonly X_START = 'xStart'
    readonly X_END = 'xEnd'
    readonly Y_START = 'yStart'
    readonly Y_END = 'yEnd'
    plotData: PlotData
    formIsVisible = false
    private readonly destroy$ = new Subject<void>()

    validateXAndY: ValidatorFn = (): ValidationErrors | null => {
        const xStart = this.rectangularSelectionForm?.get(this.X_START)?.value
        const xEnd = this.rectangularSelectionForm?.get(this.X_END)?.value
        const yStart = this.rectangularSelectionForm?.get(this.Y_START)?.value
        const yEnd = this.rectangularSelectionForm?.get(this.Y_END)?.value
        if (xStart >= xEnd) {
            return { 'xStart has to be smaller then xEnd': true }
        }
        if (yStart >= yEnd) {
            return { 'yStart has to be smaller then yEnd': true }
        }
        return null
    }

    constructor(private readonly formBuilder: FormBuilder, private store: Store) {
        this.rectangularSelectionForm = this.formBuilder.group({
            [this.X_START]: new FormControl(null, [Validators.required]),
            [this.X_END]: new FormControl(null, [Validators.required]),
            [this.Y_START]: new FormControl(null, [Validators.required]),
            [this.Y_END]: new FormControl(null, [Validators.required]),
        })

        this.rectangularSelectionForm.addValidators([this.validateXAndY])

        plotData$.pipe(takeUntil(this.destroy$)).subscribe(data => {
            this.plotData = data
        })
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    selectData(): void {
        const formRawValues = this.rectangularSelectionForm.getRawValue()
        const normalizedXStart = this.normalizeNumber(formRawValues.xStart, this.plotData.domainX)
        const normalizedXEnd = this.normalizeNumber(formRawValues.xEnd, this.plotData.domainX)
        const normalizedYStart = this.normalizeNumber(formRawValues.yStart, this.plotData.domainY)
        const normalizedYEnd = this.normalizeNumber(formRawValues.yEnd, this.plotData.domainY)
        if (normalizedXStart && normalizedXEnd && normalizedYStart && normalizedYEnd) {
            const selectedPoints = this.plotData.points.filter(
                v =>
                    v[1] >= normalizedXStart &&
                    v[1] <= normalizedXEnd &&
                    v[2] >= normalizedYStart &&
                    v[2] <= normalizedYEnd
            )
            const selectedIds = selectedPoints.map(v => v[0])
            this.store.dispatch(
                selectionActions.updateSelection({
                    points: selectedIds,
                })
            )
        }
    }

    normalizeNumber(numberToNormalize: number, domain: DomainInfo | null): number | undefined {
        if (domain) {
            return (
                (2 * (numberToNormalize - domain.lowerBound)) /
                    (domain.upperBound - domain.lowerBound) -
                1
            )
        }
        return undefined
    }

    resetFrom(): void {
        this.rectangularSelectionForm.reset()
    }

    displayForm(): void {
        this.formIsVisible = !this.formIsVisible
    }
}
